'use client';

import { CaseStudyBody } from '@/components/case-study/case-study-body';
import { CaseStudyHero } from '@/components/case-study/case-study-hero';
import { CaseStudyQuote } from '@/components/case-study/case-study-quote';
import { CustomerVideos } from '@/components/sections/customer-videos';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

/**
 * Prêts is the one story in the set with no stats row and no results table.
 * That is deliberate, not an omission: Prêts is early in the rollout and
 * explicitly asked not to have numbers published yet ("in a few months we will
 * have real figures on resolution, response times and applications completed
 * outside office hours"). `CaseStudyHero.stats` is optional, so the hero
 * simply carries none — a placeholder metric here would be an invented claim.
 * Add the stats row, the results table and the six-place number check from the
 * customer-stories skill in the same commit that lands the real figures.
 */
const PretsCaseStudy = () => {
  const { t } = useTranslation();
  return (
    <>
      <CaseStudyHero
        customerName="Prêts"
        title={t.customerStories['prets_hero_title']}
        subtitle={t.customerStories['prets_what_we_did']}
      />

      <CaseStudyBody
        aside={{
          logoAlt: 'Prêts',
          customerName: 'Prêts',
          company: t.customerStories['prets_company_description'],
          industry: t.customerStories['prets_industry'],
          websiteUrl: 'https://www.prets.io',
          extras: [
            {
              label: t.customerStories.Location,
              value: t.customerStories['prets_location'],
            },
          ],
          keyResults: [
            t.customerStories['prets_key_result_1'],
            t.customerStories['prets_key_result_2'],
            t.customerStories['prets_key_result_3'],
            t.customerStories['prets_key_result_4'],
          ],
        }}
      >
        <p>{t.customerStories['prets_intro']}</p>

        <h2>{t.customerStories['prets_challenge_title']}</h2>
        <p>{t.customerStories['prets_challenge_p1']}</p>
        <p>{t.customerStories['prets_challenge_p2']}</p>
        <p>{t.customerStories['prets_challenge_p3']}</p>

        <h2>{t.customerStories['prets_solution_title']}</h2>
        <p>{t.customerStories['prets_solution_p1']}</p>
        <p>{t.customerStories['prets_solution_p2']}</p>
        <p>{t.customerStories['prets_solution_p3']}</p>
        <p>{t.customerStories['prets_solution_p4']}</p>

        <CaseStudyQuote
          quote={[
            t.customerStories['prets_quote_p1'],
            t.customerStories['prets_quote_p2'],
            t.customerStories['prets_quote_p3'],
          ]}
          attribution={t.customerStories['prets_quote_attribution']}
        />

        <h2>{t.customerStories['prets_impact_title']}</h2>
        <p>{t.customerStories['prets_impact_p1']}</p>
        <p>{t.customerStories['prets_impact_p2']}</p>
      </CaseStudyBody>

      <CustomerVideos />

      <Testimonials />
    </>
  );
};

export default PretsCaseStudy;
